import { useEffect, useState } from 'react'
import { useHubLang } from '../lib/i18n'
import { HOME } from '../lib/hubCopy'
import { loadVideoViews, VIEW_STAGES, type ViewSummary } from '../lib/videoViews'

/**
 * 198 (David, 14 Oct): how many times the video has been watched, above the
 * card, for whole-company analytics.read only (comms_admin unscoped,
 * tenant_admin) — the people who already read aggregate reach (126).
 *
 * Four totals, each with today's figure (today = the UTC day the database
 * files a view under). There is no "people" number, on purpose: counting
 * people means recording who watched, and rule 8 forbids that. The note says
 * so in one line, so nobody reads "Started 312" as 312 colleagues.
 *
 * A read that fails draws nothing: this is a reach signal for a handful of
 * people, and an error box above the launch video would be the louder fault.
 */
export function VideoViews({ videoId, locale, refreshKey = 0 }: { videoId: string; locale: string; refreshKey?: number }) {
  const [summary, setSummary] = useState<ViewSummary | null>(null)
  useEffect(() => {
    let live = true
    loadVideoViews(videoId)
      .then((s) => live && setSummary(s))
      .catch(() => live && setSummary(null))
    return () => {
      live = false
    }
  }, [videoId, refreshKey])
  if (!summary) return null
  return <VideoViewsLine summary={summary} locale={locale} />
}

/** The strip itself, from a summary — separate so the suite can render it. */
export function VideoViewsLine({ summary, locale }: { summary: ViewSummary; locale: string }) {
  const h = HOME[useHubLang()]
  const fmt = (n: number) => {
    try {
      return new Intl.NumberFormat(locale).format(n)
    } catch {
      return String(n)
    }
  }
  return (
    <section className="videoviews" aria-label={h.videoViewsTitle}>
      <p className="videoviews__title">{h.videoViewsTitle}</p>
      {summary.since === null ? (
        <p className="videoviews__none">{h.videoViewsNone}</p>
      ) : (
        <dl className="videoviews__list">
          {VIEW_STAGES.map((stage) => (
            <div className="videoviews__stage" key={stage}>
              <dt>{h.videoViewsStages[stage]}</dt>
              <dd>
                <span className="videoviews__n">{fmt(summary.total[stage])}</span>{' '}
                <span className="videoviews__today">{h.videoViewsToday(fmt(summary.today[stage]))}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}
      <p className="videoviews__note">{h.videoViewsNote}</p>
    </section>
  )
}
